import { ConfigConstants } from '../../config/config-constants';
import { isDefined } from '../../utilities/is-defined';
import type { ODataLongText } from '../odata-types/multiline-text.type';
import type { ODataEntityMetadata } from '../odata-types/odata.type';
import type {
    ODataNotificationCode,
    ODataNotificationContract,
    ODataNotificationDop,
    ODataNotificationEmail,
    ODataNotificationEquipment,
    ODataNotificationHistory,
    ODataNotificationPaperwork,
    ODataNotificationPart,
    ODataNotificationParticipant,
    ODataNotificationTask,
} from '../odata-types/notification.type';
import type {
    NotificationCodeState,
    NotificationContractRowState,
    NotificationDropOffPointState,
    NotificationEmailState,
    NotificationEquipmentState,
    NotificationHistoryEntryState,
    NotificationLongTextLineState,
    NotificationPaperworkQuestionState,
    NotificationParticipantState,
    NotificationPartState,
    NotificationTaskState,
} from './notification-entities.state';
import { NotificationIdState } from '../utilities/notification-id.type';
import { outboundAddrNumber } from '../address';

/**
 * The inverse of `odata-to-notification-entities.ts`: one stored row back in the `NdpHeaderSet` form.
 *
 * This exists for one caller only — `notificationStateToOdata`, which rebuilds a whole payload so the
 * frozen `NotificationModel` can be built from a stored notification (see the doc comment on
 * `legacy-notification-model.bridge.ts`) — and it is deleted together with that model. Nothing else
 * may use it: a *new* reader takes the state rows as they are.
 *
 * The round trip lands where the model expects it because the conversion only ever normalized a value
 * the model normalizes the same way: a number goes back to its string form (and a field the backend
 * left empty back to `''`, which is what the model's own `parseInt`/`moment` guards expect), a flag
 * back to the character that means `true`, and a date back to the OData V2 literal the model's
 * `moment(…)` reads. What the payload carried and the slice deliberately does not keep — the OData
 * envelope and the identifier repeated on every row — is filled in from
 * {@link syntheticRowMetadata} and from the notification's own identifier, neither of which any
 * model reads back out.
 */

/** Enough of an entity metadata block to satisfy a row type; no model reads it. */
function syntheticRowMetadata<TEntityType extends string>(type: TEntityType): ODataEntityMetadata<TEntityType> {
    return { id: '', uri: '', type };
}

/**
 * A numeric field back in the string form the payload sends it in. `undefined` — what the conversion
 * produces for a field the backend left empty — becomes the empty string again, which is what the
 * legacy models' `parseInt` guards expect.
 *
 * @param value the parsed number
 */
export function numericField(value: number | undefined): string {
    return isDefined(value) ? String(value) : '';
}

/**
 * A date back in the OData V2 literal the payload sends it in, or `''` when there is none — where the
 * payload had `null`, which parses to the same invalid moment.
 *
 * @param value the parsed date, in epoch milliseconds
 */
function sapDateField(value: number | undefined): string {
    return isDefined(value) ? `/Date(${value})/` : '';
}

/**
 * A boolean back as the backend's character: the "true" character when set, `''` otherwise.
 *
 * @param value the parsed flag
 * @param trueValue the character that means `true`, `'X'` by default
 */
function sapFlagField(value: boolean, trueValue: string = ConfigConstants.sapTrue): string {
    return value ? trueValue : ConfigConstants.sapFalse;
}

/**
 * One stored code back as a `CodeSet` row.
 *
 * @param code the stored code
 */
export function notificationCodeToOdata(code: NotificationCodeState): ODataNotificationCode {
    return {
        __metadata: syntheticRowMetadata('HC_SM_NGM_58_SRV.Code'),
        Counter: code.position,
        CodeType: code.codeType,
        Qkatart: code.catalogType,
        CodeGroup: code.codeGroup,
        CodeName: code.codeName,
        CodeDesc: code.codeDescription,
    };
}

/**
 * One stored contract row back as a `ContractSet` row.
 *
 * @param contract the stored row
 * @param id the notification the row belongs to
 */
export function notificationContractRowToOdata(
    contract: NotificationContractRowState,
    id: NotificationIdState,
): ODataNotificationContract {
    return {
        __metadata: syntheticRowMetadata('HC_SM_NGM_58_SRV.Contract'),
        Counter: contract.position,
        ContLabel: contract.label,
        ContValue: contract.value,
        NotificationNo: id.qmNum,
        TaskNumber: id.maNum,
    };
}

/**
 * One stored drop-off point back as a `DopSet` row.
 *
 * @param dropOffPoint the stored drop-off point
 * @param id the notification the row belongs to
 */
export function notificationDropOffPointToOdata(
    dropOffPoint: NotificationDropOffPointState,
    id: NotificationIdState,
): ODataNotificationDop {
    return {
        __metadata: syntheticRowMetadata('HC_SM_NGM_58_SRV.Dop'),
        PONum: numericField(dropOffPoint.purchaseOrderNumber),
        DropOfPoint: dropOffPoint.dropOffPoint,
        Name1: dropOffPoint.name1,
        Name2: dropOffPoint.name2,
        Department: dropOffPoint.department,
        Room: dropOffPoint.room,
        City: dropOffPoint.city,
        PostalCode: dropOffPoint.postalCode,
        Street: dropOffPoint.street,
        AddInfo: dropOffPoint.additionalInfo,
        Building: dropOffPoint.building,
        Floor: dropOffPoint.floor,
        State: dropOffPoint.state,
        Telephone: dropOffPoint.phone,
        AddrNumber: outboundAddrNumber(dropOffPoint.addressNumber),
        Counter: numericField(dropOffPoint.position),
        NotificationNo: id.qmNum,
        TaskNumber: id.maNum,
    };
}

/**
 * One stored communication address back as an `EmailSet` row.
 *
 * @param email the stored address
 * @param id the notification the row belongs to
 */
export function notificationEmailToOdata(
    email: NotificationEmailState,
    id: NotificationIdState,
): ODataNotificationEmail {
    return {
        __metadata: syntheticRowMetadata('HC_SM_NGM_58_SRV.Email'),
        Counter: email.position,
        FaxEmail: email.faxOrEmail,
        CommCategory: email.communicationCategory,
        NotificationNo: id.qmNum,
        TaskNumber: id.maNum,
    };
}

/**
 * One stored equipment back as an `EquipmentsSet` row.
 *
 * @param equipment the stored equipment
 */
export function notificationEquipmentToOdata(equipment: NotificationEquipmentState): ODataNotificationEquipment {
    return {
        __metadata: syntheticRowMetadata('HC_SM_NGM_58_SRV.Equipments'),
        EquipmentNum: equipment.equipmentNumber,
        TechIdentNum: equipment.technicalIdentificationNumber,
        MEIndicator: equipment.mainEquipmentIndicator,
        ManPartNum: equipment.manufacturerPartNumber,
        MaterialNum: equipment.materialNumber,
        SerialNum: equipment.serialNumber,
        WarrantyEndDate: sapDateField(equipment.warrantyEndDate),
        Division: equipment.division,
        EquiShortDesc: equipment.shortDescription,
        ContractType: equipment.contractType,
        Mk: equipment.mk,
        Tk: equipment.tk,
        MkV2: equipment.mkV2,
        MeasuringPoint: equipment.measuringPoint,
        MeasuringPointDesc: equipment.measuringPointDescription,
        IntCharacteristic: equipment.internalCharacteristic,
        ExtUnit: equipment.externalUnit,
        Mandatory: equipment.mandatory,
        ServiceKey: equipment.serviceKey,
        BarcodeMaterial: equipment.barcodeMaterial,
        BarcodeSernr: equipment.barcodeSerialNumber,
        IpAddress: equipment.ipAddress,
        HostId: equipment.hostId,
        Dongle: equipment.dongle,
        MacAdresse: equipment.macAddress,
        SoftwareVers: equipment.softwareVersion,
        EndOfDeliveryDate: sapDateField(equipment.endOfDeliveryDate),
        EndOfSupportDate: sapDateField(equipment.endOfSupportDate),
        Counter: equipment.position,
    };
}

/**
 * One stored history entry back as a `HistorySet` row. Its identifier is the row's own — the earlier
 * notification it stands for — so it comes from the entry and not from the notification.
 *
 * @param history the stored history entry
 */
export function notificationHistoryEntryToOdata(history: NotificationHistoryEntryState): ODataNotificationHistory {
    return {
        __metadata: syntheticRowMetadata('HC_SM_NGM_58_SRV.History'),
        ShortText: history.shortText,
        NotificationType: history.notificationType,
        CreatedDate: history.createdDate,
        CreatedOn: history.createdTime,
        CSEName: history.cseName,
        Outstanding: numericField(history.outstanding),
        NotificationNo: history.notificationNumber,
        TaskNumber: history.taskNumber,
        Counter: numericField(history.position),
    };
}

/**
 * One stored part back as a `PartSet` row.
 *
 * @param part the stored part
 */
export function notificationPartToOdata(part: NotificationPartState): ODataNotificationPart {
    return {
        __metadata: syntheticRowMetadata('HC_SM_NGM_58_SRV.Part'),
        Counter: numericField(part.position),
        PONumber: part.purchaseOrderNumber,
        PONumberOld: part.previousPurchaseOrderNumber,
        PreviousOrder: numericField(part.previousOrder),
        ItemNumber: part.itemNumber,
        Material: part.materialNumber,
        DescText: part.description,
        Quantity: numericField(part.quantity),
        Amount: numericField(part.amount),
        Cost: numericField(part.cost),
        RepPrice: numericField(part.repairPrice),
        CustomerPrice: numericField(part.customerPrice),
        Currency: part.currency,
        CustomerCurrency: part.customerCurrency,
        MatMerkmal: part.materialCharacteristic,
        ExtUnit: part.externalUnit,
        Awbnr: part.airWaybillNumber,
        BatchNumber: part.batchNumber,
        BatchNumberLegacy: part.batchNumberLegacy,
        SparepartTxt: part.sparePartText,
        Strahler: part.strahler,
        RFrist: sapDateField(part.returnDeadline),
        XRayDateOld: sapDateField(part.xRayDateOld),
        EodDate: sapDateField(part.endOfDeliveryDate),
        EosDate: sapDateField(part.endOfSupportDate),
        TubeIndicator: part.tubeIndicator,
        RadioactIndicat: part.radioactiveIndicator,
        SerialPIndicat: part.serialPartIndicator,
        LocalScrapReturnC: part.localScrapReturnC,
        LocalScrapReturnD: part.localScrapReturnD,
        LocalScrapReturnV: part.localScrapReturnV,
        LocalScrapReturnX: part.localScrapReturnX,
        LsCrChangeable: part.localScrapReturnChangeable,
        UCRNumber: part.ucrNumber,
        UNNumber: part.unNumber,
        DangerousGoodsIndicator: part.dangerousGoodsIndicator,
        HazardousMaterial: part.hazardousMaterial,
        PROFText: part.dangerousGoodsText,
        STOFText: part.hazardousMaterialText,
        Billing: part.billing,
        // Only the "not changeable" case has a character of its own; see the conversion.
        BillingChangeable: part.billingChangeable ? '' : ConfigConstants.MATERIAL_BILLABLE_CHANGEABLE.No,
        SerialNumberOld: part.serialNumberOld,
        SerialNumberNew: part.serialNumberNew,
        TextLine: part.textLine,
        Repairable: sapFlagField(part.repairable, ConfigConstants.materialRepairableIndicator),
        QualityPart: part.qualityPart,
        SPQDocID: part.spqDocumentId,
        EnableFDA2579: sapFlagField(part.fda2579Enabled),
        MedPhysicsFlag: sapFlagField(part.medPhysicsRelevant),
    };
}

/**
 * One stored participant back as a `ParticipantSet` row.
 *
 * @param participant the stored participant
 * @param id the notification the row belongs to
 */
export function notificationParticipantToOdata(
    participant: NotificationParticipantState,
    id: NotificationIdState,
): ODataNotificationParticipant {
    return {
        __metadata: syntheticRowMetadata('HC_SM_NGM_58_SRV.Participant'),
        Counter: participant.position,
        Title: participant.title,
        FirstName: participant.firstName,
        LastName: participant.lastName,
        Email: participant.email,
        MobileNumber: participant.mobileNumber,
        TRDate: sapDateField(participant.trainingDate),
        TrainingName: participant.trainingName,
        Shared: participant.shared,
        TRFname: participant.trainerFirstName,
        TRLname: participant.trainerLastName,
        Duration: participant.duration,
        ProductName: participant.productName,
        NotificationNo: id.qmNum,
        TaskNumber: id.maNum,
        CreditFlag: participant.creditFlag,
    };
}

/**
 * One stored task back as a `TaskSet` row. `TaskNumber` is the task's own number, so only
 * `NotificationNo` comes from the notification.
 *
 * @param task the stored task
 * @param id the notification the row belongs to
 */
export function notificationTaskToOdata(task: NotificationTaskState, id: NotificationIdState): ODataNotificationTask {
    return {
        __metadata: syntheticRowMetadata('HC_SM_NGM_58_SRV.Task'),
        Counter: task.position,
        NotificationNo: id.qmNum,
        TaskNumber: task.taskNumber,
        TaskCode: task.statusCode,
        PlannedStartDate: task.plannedStartDate,
        PlannedStartTime: task.plannedStartTime,
        PlannedFinishDate: task.plannedEndDate,
        PlannedFinishTime: task.plannedEndTime,
    };
}

/**
 * One stored paperwork question back as a `PaperWorkSet` row.
 *
 * @param question the stored question
 */
export function notificationPaperworkQuestionToOdata(
    question: NotificationPaperworkQuestionState,
): ODataNotificationPaperwork {
    return {
        SapId: question.sapId,
        Required: question.required,
        ResponseOptions: question.responseOptions,
        FilePrefix: question.filePrefix,
        AlreadyUploaded: question.alreadyUploaded,
        FileName: question.fileName,
        Previousresponse: question.previousResponse,
    };
}

/**
 * The lines of one stored long text back as `*LtextSet` rows.
 *
 * A line normally carries its own `notificationNumber`/`taskNumber` — see
 * {@link NotificationLongTextLineState} — so `id` is only the fallback for a line that does not: one
 * built inside the app rather than read from the backend, or state persisted before those fields
 * existed.
 *
 * @param lines the stored lines
 * @param id the notification to fall back to when a line carries no number of its own
 */
export function notificationLongTextLinesToOdata(
    lines: readonly NotificationLongTextLineState[],
    id: NotificationIdState,
): ODataLongText[] {
    return lines.map((line) => ({
        __metadata: syntheticRowMetadata(''),
        NotificationNo: line.notificationNumber ?? id.qmNum,
        TaskNumber: line.taskNumber ?? id.maNum,
        TextID: line.textId,
        Counter: line.lineNumber,
        TextLine: line.text,
    }));
}
